import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  Snackbar,
  IconButton,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Receipt as ReceiptIcon,
  Inventory as InventoryIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../../services/firebase/config";

function GroceryItemManagement() {
  const user = useSelector((state) => state.auth.user);
  const isAdmin = useSelector((state) => state.auth.isAdmin);

  const [loading, setLoading] = useState(false);
  const [groceryItems, setGroceryItems] = useState([]);
  const [groceryOrders, setGroceryOrders] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [tabValue, setTabValue] = useState(0);
  const [statusUpdateDialog, setStatusUpdateDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [newStatus, setNewStatus] = useState("");

  const [itemData, setItemData] = useState({
    name: "",
    price: "",
    quantity: "",
    description: "",
    category: "Produce",
    imageUrl: "",
    sellerId: "",
    sellerName: "",
    available: true,
  });

  const categories = [
    "Produce",
    "Dairy",
    "Meat & Seafood",
    "Bakery",
    "Pantry",
    "Frozen",
    "Beverages",
    "Snacks",
    "Other",
  ];

  const orderStatuses = [
    "pending",
    "confirmed",
    "preparing",
    "ready",
    "delivered",
    "cancelled",
  ];

  // Redirect if not admin
  if (!user || !isAdmin) {
    return <Navigate to="/" replace />;
  }

  // Fetch grocery items
  useEffect(() => {
    const q = query(collection(db, "groceryItems"), orderBy("name"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setGroceryItems(items);
    });

    return () => unsubscribe();
  }, []);

  // Fetch grocery orders
  useEffect(() => {
    const q = query(
      collection(db, "groceryOrders"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const orders = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setGroceryOrders(orders);
    });

    return () => unsubscribe();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setItemData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setItemData({
        name: item.name || "",
        price: item.price?.toString() || "",
        quantity: item.quantity?.toString() || "",
        description: item.description || "",
        category: item.category || "Produce",
        imageUrl: item.imageUrl || "",
        sellerId: item.sellerId || "",
        sellerName: item.sellerName || "",
        available: item.available !== false,
      });
    } else {
      setEditingItem(null);
      setItemData({
        name: "",
        price: "",
        quantity: "",
        description: "",
        category: "Produce",
        imageUrl: "",
        sellerId: "",
        sellerName: "",
        available: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const itemPayload = {
        ...itemData,
        price: Number(itemData.price),
        quantity: Number(itemData.quantity),
        updatedAt: serverTimestamp(),
      };

      if (editingItem) {
        // Update existing item
        await updateDoc(doc(db, "groceryItems", editingItem.id), itemPayload);
        showSnackbar("Grocery item updated successfully!");
      } else {
        // Create new item
        await addDoc(collection(db, "groceryItems"), {
          ...itemPayload,
          createdAt: serverTimestamp(),
          addedBy: user.uid,
        });
        showSnackbar("Grocery item added successfully!");
      }

      handleCloseDialog();
    } catch (error) {
      console.error("Error saving grocery item:", error);
      showSnackbar("Error saving grocery item: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (itemId, itemName) => {
    if (window.confirm(`Are you sure you want to delete "${itemName}"?`)) {
      try {
        await deleteDoc(doc(db, "groceryItems", itemId));
        showSnackbar("Grocery item deleted successfully!");
      } catch (error) {
        console.error("Error deleting grocery item:", error);
        showSnackbar("Error deleting grocery item: " + error.message, "error");
      }
    }
  };

  const handleToggleAvailability = async (item) => {
    try {
      await updateDoc(doc(db, "groceryItems", item.id), {
        available: !item.available,
        updatedAt: serverTimestamp(),
      });
      showSnackbar(
        `Item ${!item.available ? "enabled" : "disabled"} successfully!`
      );
    } catch (error) {
      console.error("Error updating availability:", error);
      showSnackbar("Error updating availability: " + error.message, "error");
    }
  };

  // Order management functions
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleStatusUpdate = (order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setStatusUpdateDialog(true);
  };

  const handleStatusSave = async () => {
    if (!selectedOrder || !newStatus) return;

    try {
      await updateDoc(doc(db, "groceryOrders", selectedOrder.id), {
        status: newStatus,
        updatedAt: serverTimestamp(),
      });
      showSnackbar("Order status updated successfully!");
      setStatusUpdateDialog(false);
      setSelectedOrder(null);
    } catch (error) {
      console.error("Error updating order status:", error);
      showSnackbar("Error updating order status: " + error.message, "error");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "confirmed":
        return "info";
      case "preparing":
        return "primary";
      case "ready":
        return "success";
      case "delivered":
        return "success";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const formatOrderDate = (timestamp) => {
    if (!timestamp) return "Unknown";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" mb={3}>
        Grocery Store Management
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab
            icon={<InventoryIcon />}
            label="Items Management"
            iconPosition="start"
          />
          <Tab
            icon={<ReceiptIcon />}
            label={`Orders (${groceryOrders.length})`}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Items Management Tab */}
      {tabValue === 0 && (
        <>
          <Box display="flex" justifyContent="flex-end" mb={3}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add New Item
            </Button>
          </Box>

          {/* Items Grid */}
          <Grid container spacing={3}>
            {groceryItems.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Card>
                  {item.imageUrl && (
                    <CardMedia
                      component="img"
                      height="200"
                      image={item.imageUrl}
                      alt={item.name}
                    />
                  )}
                  <CardContent>
                    <Typography variant="h6" component="div" gutterBottom>
                      {item.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {item.description}
                    </Typography>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      my={1}
                    >
                      <Typography variant="h6" color="primary">
                        {item.price?.toFixed(2)} ND
                      </Typography>
                      <Chip
                        label={item.category}
                        size="small"
                        color="secondary"
                      />
                    </Box>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      my={1}
                    >
                      <Typography variant="body2">
                        Stock: {item.quantity}
                      </Typography>
                      <Chip
                        label={item.available ? "Available" : "Unavailable"}
                        size="small"
                        color={item.available ? "success" : "error"}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Seller: {item.sellerName || "Unknown"}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleOpenDialog(item)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color={item.available ? "warning" : "success"}
                      onClick={() => handleToggleAvailability(item)}
                    >
                      {item.available ? "Disable" : "Enable"}
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDelete(item.id, item.name)}
                    >
                      Delete
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {groceryItems.length === 0 && (
            <Paper sx={{ p: 3, textAlign: "center", mt: 3 }}>
              <Typography variant="h6" color="text.secondary">
                No grocery items found. Add your first item to get started!
              </Typography>
            </Paper>
          )}
        </>
      )}

      {/* Orders Management Tab */}
      {tabValue === 1 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groceryOrders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell>#{order.id.slice(-8)}</TableCell>
                  <TableCell>{order.userName || order.userEmail}</TableCell>
                  <TableCell>
                    <Box>
                      {order.items?.map((item, index) => (
                        <Typography
                          key={index}
                          variant="caption"
                          display="block"
                        >
                          {item.quantity}x {item.name}
                        </Typography>
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>{order.total?.toFixed(2)} ND</TableCell>
                  <TableCell>
                    <Chip
                      label={order.status}
                      color={getStatusColor(order.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatOrderDate(order.createdAt)}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleStatusUpdate(order)}
                    >
                      Update Status
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {groceryOrders.length === 0 && (
            <Box p={3} textAlign="center">
              <Typography variant="h6" color="text.secondary">
                No orders found yet.
              </Typography>
            </Box>
          )}
        </TableContainer>
      )}

      {/* Status Update Dialog */}
      <Dialog
        open={statusUpdateDialog}
        onClose={() => setStatusUpdateDialog(false)}
      >
        <DialogTitle>Update Order Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={newStatus}
              label="Status"
              onChange={(e) => setNewStatus(e.target.value)}
            >
              {orderStatuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusUpdateDialog(false)}>Cancel</Button>
          <Button onClick={handleStatusSave} variant="contained">
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingItem ? "Edit Grocery Item" : "Add New Grocery Item"}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Item Name"
                  name="name"
                  value={itemData.name}
                  onChange={handleInputChange}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Price ($)"
                  name="price"
                  type="number"
                  step="0.01"
                  value={itemData.price}
                  onChange={handleInputChange}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Quantity Available"
                  name="quantity"
                  type="number"
                  value={itemData.quantity}
                  onChange={handleInputChange}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  multiline
                  rows={3}
                  value={itemData.description}
                  onChange={handleInputChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={itemData.category}
                    label="Category"
                    onChange={handleInputChange}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={itemData.available}
                      onChange={handleInputChange}
                      name="available"
                    />
                  }
                  label="Available for purchase"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Image URL"
                  name="imageUrl"
                  value={itemData.imageUrl}
                  onChange={handleInputChange}
                  placeholder="https://example.com/image.jpg"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Seller ID"
                  name="sellerId"
                  value={itemData.sellerId}
                  onChange={handleInputChange}
                  placeholder="seller-123"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Seller Name"
                  name="sellerName"
                  value={itemData.sellerName}
                  onChange={handleInputChange}
                  placeholder="Local Market"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} startIcon={<CancelIcon />}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={20} /> : <SaveIcon />
              }
            >
              {loading ? "Saving..." : editingItem ? "Update Item" : "Add Item"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default GroceryItemManagement;
